import { useCallback, useEffect, useRef } from 'react';
import { cancelAnimation, makeMutable, type SharedValue } from 'react-native-reanimated';

import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { trackExtractedQuestionsRendered } from '@/util/analyticsEvents';

interface UseExtractedQuestionsBubbleParams {
  messageSid: string;
  questionCount: number;
}

export interface UseExtractedQuestionsBubbleReturn {
  pulseQuestion: (questionIndex: number) => void;
  /** Returns the per-question pulse tick SharedValue. The card's pulse animation
   * hook subscribes to this directly via `useAnimatedReaction`, keeping the
   * trigger off the React + store path. */
  getPulseTick: (questionIndex: number) => SharedValue<number>;
}

/**
 * Lazily allocates one pulse-tick SharedValue per question index, outside the
 * hook list.
 *
 * An earlier version called `useSharedValue` in a loop over `questionCount`,
 * on the assumption that the count is fixed once a message arrives. It is not:
 * a bubble re-renders with a different `extractedQuestions.length` for the same
 * `messageSid`, so the hook count moved with it and corrupted React's hook
 * list — surfacing in production as "Should have a queue. You are likely
 * calling Hooks conditionally" and as a TypeError reading `length` inside
 * React's own `areHookInputsEqual`.
 *
 * `useSharedValue` is `makeMutable` plus a `cancelAnimation` on unmount, so
 * allocating that way here keeps identical value semantics while making this
 * hook's hook count constant. Allocating on demand also means an index beyond
 * the count at mount returns a real SharedValue rather than `undefined`.
 *
 * The map is never cleared, only cancelled. A tick's identity has to hold for
 * as long as the subtree lives: the card's pulse hook captures the object into
 * a `useAnimatedReaction` worklet whose deps do not include it, so handing out
 * a fresh mutable for an index already being watched would leave the reaction
 * subscribed to the old one and kill the pulse with no error to show for it.
 */
function usePulseTicks(): (questionIndex: number) => SharedValue<number> {
  const ticksRef = useRef(new Map<number, SharedValue<number>>());

  useEffect(() => {
    const ticks = ticksRef.current;

    return () => {
      for (const tick of ticks.values()) {
        cancelAnimation(tick);
      }
    };
  }, []);

  return useCallback((questionIndex: number): SharedValue<number> => {
    const existing = ticksRef.current.get(questionIndex);

    if (existing) {
      return existing;
    }

    const tick = makeMutable(0);

    ticksRef.current.set(questionIndex, tick);

    return tick;
  }, []);
}

/**
 * Single wiring hook for a per-message extracted-questions bubble.
 *
 * Fires the impression event and pulses a question's card when its highlight
 * or chip is pressed. The cards are always shown, so a press only draws the
 * eye to the matching one. Pulse triggers run through Reanimated SharedValues
 * (one per question, allocated on first use), off the React render path.
 *
 * `questionCount` does not have to be stable across renders — see
 * `usePulseTicks` for why that once mattered and no longer does. It is still
 * read once for the impression event, so a bubble whose questions arrive after
 * mount reports the count it first rendered with; correcting that is an
 * analytics-semantics decision, not a rendering one.
 */
export function useExtractedQuestionsBubble({
  messageSid,
  questionCount,
}: UseExtractedQuestionsBubbleParams): UseExtractedQuestionsBubbleReturn {
  const { track } = useChatAnalytics();

  const hasFiredImpressionRef = useRef(false);

  useEffect(() => {
    if (hasFiredImpressionRef.current) {
      return;
    }

    hasFiredImpressionRef.current = true;

    trackExtractedQuestionsRendered(track, {
      messageSid,
      questionCount,
    });
  }, [messageSid, questionCount, track]);

  const getPulseTick = usePulseTicks();

  const pulseQuestion = useCallback(
    (questionIndex: number) => {
      const tick = getPulseTick(questionIndex);

      tick.value = tick.value + 1;
    },
    [getPulseTick]
  );

  return { pulseQuestion, getPulseTick };
}
